/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';
import { createResourceLocatorString } from '../../../../util/routes';

import {
  AvatarLarge,
  ExternalLink,
  IconArrowRight,
  IconSocialMediaFacebook,
  IconSocialMediaInstagram,
  InlineTextButton,
  NamedLink,
} from '../../../../components';

import Drawer from 'react-modern-drawer';
import './Drawer.css';
import css from './TopbarMobileMenu.module.css';
import IconSocialMediaTiktok from '../../../../components/IconSocialMediaTiktok/IconSocialMediaTiktok';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <li className={className}>
        <NamedLink name={name} params={params} to={to}>
          <span className={css.menuItemBorder} />
          {text}
        </NamedLink>
      </li>
    );
  }
  return (
    <li className={css.navigationLink}>
      <ExternalLink href={href}>
        <span className={css.menuItemBorder} />
        {text}
      </ExternalLink>
    </li>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab = 'sales',
    currentUser,
    customLinks,
    onLogout,
    showCreateListingsLink,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const user = ensureCurrentUser(currentUser);
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const handleRedirect = key => {
    const isWarmup = key === 'warmups';
    const isLeo = key === 'leotards';
    const isPointe = key === 'pointeShoes';

    const warmupSearchParams = {
      pub_type: 'Warmups',
      pub_category: 'dancewear',
    };

    const leoSearchParams = {
      pub_type: 'Leotard',
      pub_category: 'dancewear',
    };

    const pointeSearchParams = {
      pub_type: 'pointe',
      pub_category: 'shoes',
    };

    const searchParams = {
      pub_category: key,
    };

    const params = isWarmup
      ? warmupSearchParams
      : isLeo
      ? leoSearchParams
      : isPointe
      ? pointeSearchParams
      : searchParams;
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, params));
  };

  const handleSaleRedirect = () => {
    history.push(
      createResourceLocatorString('SearchPage', routeConfiguration, {}, { meta_onSale: true })
    );
  };

  const handleCostumesRedirect = () => {
    history.push(
      createResourceLocatorString(
        'SearchPage',
        routeConfiguration,
        {},
        { pub_category: 'costumes' }
      )
    );
  };

  const handleBrandsRedirect = key => {
    const searchParams = {
      pub_brand: key,
    };

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  };
  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  const drawer = (
    <Drawer open={isOpen} onClose={() => setIsOpen(!isOpen)} direction="left">
      <div className={classNames(css.browseMenu, css.drawerMenu)}>
        <div className={css.drawerHead}>
          <span className={css.back} onClick={() => setIsOpen(!isOpen)}>
            <IconArrowRight />
          </span>
          <span className={css.cross} onClick={() => setIsOpen(!isOpen)}>
            &times;
          </span>
        </div>
        <div className={css.links}>
          <div className={css.linkItem}>
            <span onClick={() => handleBrandsRedirect('Eleve')}>
              <FormattedMessage id="TopbarMobileMenu.brandEleve" /> <IconArrowRight />
            </span>
          </div>
        </div>
        <div className={css.links}>
          <div className={css.linkItem}>
            <span onClick={() => handleBrandsRedirect('Five-Dancewear')}>
              <FormattedMessage id="TopbarMobileMenu.brandFiveDancewear" /> <IconArrowRight />
            </span>
          </div>
        </div>
        <div className={css.links}>
          <div className={css.linkItem}>
            <span onClick={() => handleBrandsRedirect('LuckyLeo')}>
              <FormattedMessage id="TopbarMobileMenu.brandLuckyLeo" /> <IconArrowRight />
            </span>
          </div>
        </div>
        <div className={css.links}>
          <div className={css.linkItem}>
            <span onClick={() => handleBrandsRedirect('Tiger-Friday')}>
              <FormattedMessage id="TopbarMobileMenu.brandTigerFriday" /> <IconArrowRight />
            </span>
          </div>
        </div>
        <div className={css.links}>
          <div className={css.linkItem}>
            <span onClick={() => handleBrandsRedirect('Yumiko')}>
              <FormattedMessage id="TopbarMobileMenu.brandYumiko" /> <IconArrowRight />
            </span>
          </div>
        </div>
      </div>
    </Drawer>
  );

  const drawerData = (
    <>
      <div className={css.browseMenu}>
        <h2>
          <FormattedMessage id="TopbarMobileMenu.browseTutulist" />
        </h2>
        {drawer}
        <div className={css.links}>
          <div className={css.linkItem}>
            <span onClick={() => handleRedirect('dancewear')}>
              <FormattedMessage id="TopbarMobileMenu.allDancewear" /> <IconArrowRight />
            </span>
          </div>
          <div className={css.linkItem}>
            <span onClick={() => handleRedirect('leotards')}>
              <FormattedMessage id="TopbarMobileMenu.leotards" /> <IconArrowRight />
            </span>
          </div>
          <div className={css.linkItem}>
            <span onClick={() => handleRedirect('warmups')}>
              <FormattedMessage id="TopbarMobileMenu.warmups" /> <IconArrowRight />
            </span>
          </div>
          <div className={css.linkItem}>
            <span onClick={() => handleRedirect('pointeShoes')}>
              <FormattedMessage id="TopbarMobileMenu.pointeShoes" /> <IconArrowRight />
            </span>
          </div>
          <div className={css.linkItem}>
            <span onClick={() => handleRedirect('shoes')}>
              <FormattedMessage id="TopbarMobileMenu.shoes" /> <IconArrowRight />
            </span>
          </div>
          <div className={css.linkItem}>
            <span onClick={() => handleCostumesRedirect()}>
              <FormattedMessage id="TopbarMobileMenu.costumes" /> <IconArrowRight />
            </span>
          </div>
          <div className={css.linkItem}>
            <span onClick={() => handleSaleRedirect()}>
              <FormattedMessage id="TopbarMobileMenu.sale" /> <IconArrowRight />
            </span>
          </div>
          <div className={css.linkItem}>
            <span onClick={() => setIsOpen(true)}>
              <FormattedMessage id="TopbarMobileMenu.topBrands" /> <IconArrowRight />
            </span>
          </div>
        </div>
      </div>
      <div className={css.moreFrom}>
        <h2>
          <FormattedMessage id="TopbarMobileMenu.moreFromTutulist" />
        </h2>
        <div className={css.links}>
          <div className={css.linkItem}>
            <NamedLink name="NewListingPage" className={css.link}>
              <FormattedMessage id="Footer.listAnItem" />
            </NamedLink>
          </div>
          <div className={css.linkItem}>
            <a className={css.link} href="/p/selltotutulist">
              <FormattedMessage id="Footer.waysToSell" />
            </a>
          </div>
          <div className={css.linkItem}>
            <a className={css.link} href="/help/selling">
              <FormattedMessage id="Footer.sellerFaq" />
            </a>
          </div>
          <div className={css.linkItem}>
            <a className={css.link} href="/help/buying">
              <FormattedMessage id="Footer.buyerFaq" />
            </a>
          </div>
          <div className={css.linkItem}>
            <a className={css.link} href="/p/about">
              <FormattedMessage id="Footer.aboutUs" />
            </a>
          </div>
          <div className={css.linkItem}>
            <a className={css.link} href="/help">
              <FormattedMessage id="Footer.helpCenter" />
            </a>
          </div>
          <div className={css.linkItem}>
            <a className={css.link} href="/contact">
              <FormattedMessage id="Footer.toContactPage" />
            </a>
          </div>
          <div className={css.linkItem}>
            <a className={css.link} href="/help/community-guidelines">
              <FormattedMessage id="Footer.communityGuidelines" />
            </a>
          </div>
          <div className={css.linkItem}>
            <a className={css.link} href="/p/terms-of-service">
              <FormattedMessage id="Footer.termsOfUse" />
            </a>
          </div>
          <div className={css.linkItem}>
            <a className={css.link} href="/p/privacy-policy">
              <FormattedMessage id="Footer.privacyPolicy" />
            </a>
          </div>
        </div>
      </div>
    </>
  );

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    // const signupOrLogin = (
    //   <span className={css.authenticationLinks}>
    //     <FormattedMessage
    //       id="TopbarMobileMenu.signupOrLogin"
    //       values={{ lineBreak: <br />, signup, login }}
    //     />
    //   </span>
    // );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <p className={css.community}>
            <FormattedMessage id="TopbarMobileMenu.joinOurCommunity" />
          </p>
          <div className={css.socialMediaLinks}>
            <a href="https://www.facebook.com/thetutulist">
              <IconSocialMediaFacebook />
            </a>
            <a href="https://www.instagram.com/tutulist">
              <IconSocialMediaInstagram />
            </a>
            <a href="https://www.tiktok.com/discover/tutulist">
              <IconSocialMediaTiktok />
            </a>
          </div>
          {login}
          {signup}
          {drawerData}
        </div>
      </div>
    );
  }

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    const isOffersPage = page === 'OffersPage' && currentPage === 'OffersSentPage';
    return currentPage === page || isAccountSettingsPage || isInboxPage || isOffersPage
      ? css.currentPage
      : null;
  };

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <li className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}>
      <NamedLink name="ManageListingsPage">
        <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
      </NamedLink>
    </li>
  ) : null;

  return (
    <div className={css.root}>
      <div className={css.content}>
        {/* <div className={css.userAccount}>
          <AvatarLarge className={css.avatar} user={currentUser} />
          <div className={css.rightSec}>
            <div className={css.socialMediaLinks}>
              <a href="https://www.facebook.com/thetutulist">
                <IconSocialMediaFacebook />
              </a>
              <a href="https://www.instagram.com/tutulist">
                <IconSocialMediaInstagram />
              </a>
              <a href="https://www.tiktok.com/discover/tutulist">
                <IconSocialMediaTiktok />
              </a>
            </div>
          </div>
        </div> */}
        <div className={css.greetingSec}>
          <span className={css.greeting}>
            <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
          </span>
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <FormattedMessage id="TopbarMobileMenu.logoutLink" />
          </InlineTextButton>
        </div>
        <div className={css.links}>
          <InlineTextButton
            className={css.linkItem}
            onClick={() =>
              history.push(
                createResourceLocatorString('ProfileSettingsPage', routeConfiguration, {}, {})
              )
            }
          >
            <FormattedMessage id="TopbarMobileMenu.yourProfile" />
          </InlineTextButton>
          {isAuthenticated ? (
            <InlineTextButton
              className={css.linkItem}
              onClick={() =>
                history.push(
                  createResourceLocatorString('DashboardPage', routeConfiguration, {}, {})
                )
              }
            >
              <FormattedMessage id="TopbarMobileMenu.DashboardPage" />
            </InlineTextButton>
          ) : null}
          <InlineTextButton
            className={css.linkItem}
            onClick={() =>
              history.push(
                createResourceLocatorString('InboxPage', routeConfiguration, { tab: inboxTab }, {})
              )
            }
          >
            <FormattedMessage id="TopbarMobileMenu.purchasesAndSales" />
          </InlineTextButton>
          <InlineTextButton
            className={css.linkItem}
            onClick={() =>
              history.push(
                createResourceLocatorString(
                  'MessagesPage',
                  routeConfiguration,
                  { tab: 'enquiry' },
                  {}
                )
              )
            }
          >
            <FormattedMessage id="TopbarMobileMenu.messagesLink" />
          </InlineTextButton>
          <InlineTextButton
            className={css.linkItem}
            onClick={() =>
              history.push(createResourceLocatorString('OffersPage', routeConfiguration, {}, {}))
            }
          >
            <FormattedMessage id="TopbarDesktop.offersLink" />
          </InlineTextButton>
          <a className={css.linkItem} href="/listings">
            <FormattedMessage id="TopbarMobileMenu.ManageListingsPageLink" />
          </a>
          <InlineTextButton
            className={css.linkItem}
            onClick={() =>
              history.push(
                createResourceLocatorString('ContactDetailsPage', routeConfiguration, {}, {})
              )
            }
          >
            <FormattedMessage id="TopbarMobileMenu.ContactDetailsPageLink" />
          </InlineTextButton>
        </div>
        {drawerData}
      </div>
    </div>
  );
};

export default TopbarMobileMenu;
